import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../api';
import { clearAuthData } from '../../utils/authStorage';
import { AuthContext } from '../../context/logeduser';
import { StyledInput, Label } from '../StyledComponents/StyledComponent';
import { useAlert } from '../../context/AlertContext';
import Illustration from '../../assets/employee_login.png';
import { useCompany } from '../../context/CompanyContext';
import LoginToggle from './LoginToggle';

export function SignUpForm() {
  const { showSuccess, showError } = useAlert();
  const company = useCompany();
  const [state, setState] = useState({ user_name: '', password: '' });
  const [resolvedEmpNo, setResolvedEmpNo] = useState(''); // employee_no returned by send-otp (needed for verify)
  const [otp, setOtp] = useState(false);
  const [otpnum, setOtpNum] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [resendIn, setResendIn] = useState(0);
  const [rateLimitIn, setRateLimitIn] = useState(0);
  const [otpDestination, setOtpDestination] = useState({ email: '', mobile: '' });
  const [delivery, setDelivery] = useState(null);
  const [error] = useState(null);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setInterval(() => {
      setOtpExpiresIn((value) => Math.max(0, value - 1));
      setResendIn((value) => Math.max(0, value - 1));
      setRateLimitIn((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const safe = Math.max(0, Number(seconds) || 0);
    return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
  };

  const requestOtp = async () => {
    clearAuthData();
    const response = await axiosInstance.post(
      'api/send-otp/',
      { companyEmail: state.user_name },
    );
    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to send OTP.');
    }
    setOtp(true);
    setResolvedEmpNo(response.data.employee_no || state.user_name);
    setOtpExpiresIn(Number(response.data.expires_in) || 0);
    setResendIn(Number(response.data.resend_after) || 0);
    setRateLimitIn(0);
    setOtpDestination({
      email: response.data.masked_email || '',
      mobile: response.data.masked_mobile || '',
    });
    setDelivery(response.data.delivery || null);
    showSuccess(response.data.message || 'OTP sent successfully.', {
      title: 'OTP Sent',
      position: 'top-center'
    });
  };

  const handleChange = (e) => {
    setState({ ...state, [e.target.name]: e.target.value });
  };

  const handleOnSubmit = async (e) => {
    e.preventDefault();

    if (!otp) {
      try {
        await requestOtp();
      } catch (err) {
        const wait = Number(err.response?.data?.retry_after) || 0;
        setRateLimitIn(wait);
        showError(
          err.response?.data?.message || err.message || "Failed to send OTP. Please try again.",
          {
            title: "Error",
            autoHideDuration: 5000
          }
        );
        console.error("Send OTP Error:", err);
      }
    } else {
      if (otpnum.length !== 6) {
        setOtpError('Please enter the complete 6-digit OTP.');
        return;
      }
      try {
        const response = await axiosInstance.post(
          'api/verify-otp/',
          {
            otp: otpnum,
            employee_no: resolvedEmpNo || state.user_name
          },
        );

        if (response.data.status === "success") {
          login(response.data);
          showSuccess("OTP verified! Redirecting...", {
            title: "Success",
            position: "top-center"
          });

          setTimeout(() => {
            navigate('/home');
          }, 1000);
        } else {
          showError(response.data.message || "Invalid OTP. Please try again.", {
            title: "Verification Failed"
          });
        }
      } catch (err) {
        showError(
          err.response?.data?.message || "Failed to verify OTP. Please try again.",
          {
            title: "Error",
            autoHideDuration: 5000
          }
        );
        console.error("Verify OTP Error:", err);
      }
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2
    }}>
      <Paper elevation={12} sx={{
        width: '100%',
        maxWidth: 1000,
        display: 'flex',
        borderRadius: 6,
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: 600 }
      }}>
        <Grid container>
          {/* Left: Form Section */}
          <Grid item xs={12} md={6} sx={{
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box mb={4} sx={{ textAlign: 'center', minHeight: 100 }}>
              {!company.loading && (
                <>
                  {company.logo && <img src={company.logo} alt={company.name || 'Company logo'} onError={(event) => { event.currentTarget.style.display = 'none'; }} style={{ height: 60, marginBottom: 24 }} />}
                  {company.name && (
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                      {company.name}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            <LoginToggle />

            <Box component="form" onSubmit={handleOnSubmit}>
              {otp ? (
                <Box mb={3}>
                  <Label>Verify OTP</Label>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {otpDestination.email || otpDestination.mobile
                      ? `OTP sent to ${[otpDestination.mobile, otpDestination.email].filter(Boolean).join(' and ')}`
                      : 'If the account is eligible, an OTP has been sent.'}
                  </Typography>
                  {otpExpiresIn > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      OTP expires in {formatTime(otpExpiresIn)}
                    </Typography>
                  )}
                  <StyledInput
                    fullWidth
                    placeholder="Enter 6-digit OTP"
                    value={otpnum}
                    onChange={(e) => {
                      // Strip anything non-numeric (typed or pasted) and cap at 6
                      // digits — the server-side OTP is always a 6-digit code.
                      setOtpNum(e.target.value.replace(/\D/g, '').slice(0, 6));
                      if (otpError) setOtpError('');
                    }}
                    inputProps={{ maxLength: 6, inputMode: 'numeric', pattern: '[0-9]*', autoComplete: 'one-time-code' }}
                  />
                  {otpError && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                      {otpError}
                    </Typography>
                  )}
                  {delivery && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Delivery: SMS {delivery.sms ? '✓' : '✕'} · Email {delivery.email ? '✓' : '✕'} · WhatsApp {delivery.whatsapp ? '✓' : '✕'}
                    </Typography>
                  )}
                  <Button
                    type="button"
                    variant="text"
                    disabled={resendIn > 0 || rateLimitIn > 0}
                    onClick={async () => {
                      try {
                        await requestOtp();
                      } catch (err) {
                        const wait = Number(err.response?.data?.retry_after) || 0;
                        setRateLimitIn(wait);
                        showError(err.response?.data?.message || 'Failed to resend OTP.');
                      }
                    }}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    {rateLimitIn > 0
                      ? `Try again in ${formatTime(rateLimitIn)}`
                      : resendIn > 0
                        ? `Resend OTP in ${formatTime(resendIn)}`
                        : 'Resend OTP'}
                  </Button>
                </Box>
              ) : (
                <Box mb={3}>
                  {/* One field accepts either an Employee ID or a registered mobile
                      number — the backend (send_otp) auto-detects which was typed. */}
                  <Label>Employee ID or Mobile Number</Label>
                  <StyledInput
                    fullWidth
                    placeholder="Enter Employee ID or mobile number"
                    name="user_name"
                    value={state.user_name}
                    onChange={handleChange}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                    Log in with your Employee ID or your registered mobile number.
                  </Typography>
                </Box>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  mt: 2,
                  py: 1.5,
                  backgroundColor: '#218838',
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#1e7e34',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(33, 136, 56, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  fontWeight: 'bold',
                  borderRadius: 3,
                }}
              >
                {otp ? 'Verify & Login' : 'Send OTP'}
              </Button>

              {error && (
                <Typography color="error" mt={2} textAlign="center" variant="body2">
                  {error}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Right: Illustration */}
          <Grid item md={6} sx={{
            display: { xs: 'none', md: 'flex' },
            background: 'linear-gradient(45deg, #e0f2f1 0%, #e1f5fe 100%)',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            position: 'relative'
          }}>
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'float 6s ease-in-out infinite'
            }}>
              <img src={Illustration} alt="illustration" style={{ maxWidth: '90%', height: 'auto' }} />
            </Box>
            <style>
              {`
                @keyframes float {
                  0% { transform: translateY(0px); }
                  50% { transform: translateY(-20px); }
                  100% { transform: translateY(0px); }
                }
              `}
            </style>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
